import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {useToggle} from '../../../../srcCommon/helpers/hookHelpers';
import {RULE_LIBRARY_CARD_WIDTH, RULE_LIBRARY_LARGE_CARD_WIDTH} from './ruleLibraryCard';
import {DisplayableRuleRecipe, RuleLibraryRecipeCard} from './ruleLibraryRecipeCard';

/*
 * Props.
 */

interface RuleLibraryCategoryProps {
  title: ReactNode;
  recipes: ReadonlyArray<DisplayableRuleRecipe>;
  sectionWidth: number;
  onShowMore?: Handler;
  imageUrl?: string;
  backgroundColor?: string;
  learnMore?: {
    text: ReactNode;
    url: string;
  };
}

/*
 * Styles.
 */

export const RULE_LIBRARY_GAP_SIZE = 16;
const SHOW_MORE_PADDING = 14;

const StyledHeader = styled.div<{$maxWidth: number}>`
  ${flexCenter};
  justify-content: space-between;
  max-width: ${(p) => p.$maxWidth}px;
`;

const StyledHeaderTitle = styled.h2`
  color: ${(p) => p.theme.greys.shade90};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.veryLarge};
`;

const StyledContent = styled.div`
  ${flexCenter};
  flex-wrap: wrap;

  & > * {
    margin-right: ${RULE_LIBRARY_GAP_SIZE}px;
    margin-bottom: ${RULE_LIBRARY_GAP_SIZE}px;
  }

  & > *:last-child {
    margin-right: 0;
  }
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledLearnMoreDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledLearnMoreIcon = styled(Icon)`
  margin-left: 4px;
`;

/*
 * Component.
 */

export const RuleLibraryCategory: FC<RuleLibraryCategoryProps> = ({
  title,
  recipes,
  sectionWidth,
  imageUrl: sectionImageUrl,
  backgroundColor: sectionBackgroundColor,
  learnMore,
}) => {
  const [showMore, toggleShowMore] = useToggle();

  const [displayableRecipesCount, resultingWidth] = getDisplayableRecipeCount(sectionWidth, recipes);
  const hasShowMore = displayableRecipesCount < recipes.length;

  const recipesToDisplay = showMore ? recipes : recipes.slice(0, Math.max(displayableRecipesCount, 1));

  return (
    <section>
      <StyledHeader $maxWidth={resultingWidth - RULE_LIBRARY_GAP_SIZE + SHOW_MORE_PADDING}>
        <StyledHeaderTitle>{title}</StyledHeaderTitle>
        <StyledButtonWrapperDiv>
          {learnMore && displayLearnMoreButton(learnMore.text, learnMore.url)}
          {maybeDisplayShowMoreButton(hasShowMore, showMore, toggleShowMore)}
        </StyledButtonWrapperDiv>
      </StyledHeader>
      <StyledContent>
        {recipesToDisplay.map((recipe, index) => {
          const displayRecipe =
            index === 0 ? computeRecipeForFirstCard(recipe, sectionImageUrl, sectionBackgroundColor) : recipe;

          return <RuleLibraryRecipeCard key={recipe.id} recipe={displayRecipe} />;
        })}
      </StyledContent>
    </section>
  );
};

/*
 * Helpers.
 */

function getRecipeCardWidth(recipe: DisplayableRuleRecipe) {
  if (recipe.imageUrl) {
    return RULE_LIBRARY_LARGE_CARD_WIDTH;
  }
  return RULE_LIBRARY_CARD_WIDTH;
}

function getDisplayableRecipeCount(rowWidth: number, recipes: ReadonlyArray<DisplayableRuleRecipe>) {
  let count = 0;
  let currentWidth = 0;
  for (let i = 0; i < recipes.length; i++) {
    const recipe = recipes[i];
    const cardWidth = getRecipeCardWidth(recipe);
    if (currentWidth + cardWidth > rowWidth) {
      break;
    }

    currentWidth += cardWidth + RULE_LIBRARY_GAP_SIZE;
    count += 1;
  }

  return [count, currentWidth];
}

function displayLearnMoreButton(learnMoreText: ReactNode, learnMoreUrl: string) {
  return (
    <Button onClick={() => openInBrowser(learnMoreUrl)} variant={ButtonVariantsEnum.LINK}>
      <StyledLearnMoreDiv>
        {learnMoreText} <StyledLearnMoreIcon name="externalLinkSmall" />
      </StyledLearnMoreDiv>
    </Button>
  );
}

function maybeDisplayShowMoreButton(hasShowMore: boolean, showMore: boolean, toggleShowMore: Handler) {
  if (!hasShowMore) {
    return null;
  }

  return (
    <Button onClick={toggleShowMore} variant={ButtonVariantsEnum.LINK}>
      {showMore ? (
        <Trans id="rule-library-show-less">Show less</Trans>
      ) : (
        <Trans id="rule-library-show-more">Show more</Trans>
      )}
    </Button>
  );
}

function computeRecipeForFirstCard(
  recipe: DisplayableRuleRecipe,
  sectionImageUrl?: string,
  sectionBackgroundColor?: string,
) {
  if (!sectionImageUrl && !sectionBackgroundColor) {
    return recipe;
  }

  const imageUrl = sectionImageUrl ?? recipe.imageUrl;
  const backgroundColor = sectionBackgroundColor ?? recipe.backgroundColor;

  return {...recipe, imageUrl, backgroundColor};
}
